import { useState, useEffect } from 'react';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/solid';
import { Header, Footer, PasswordInput } from '@ui';
import { getSupabaseClient } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check if user has visited before (check on mount, set for NEXT visit)
  const [isReturningUser] = useState(() => {
    const hasVisited = localStorage.getItem('gc_guide_visited');
    return hasVisited === 'true';
  });

  // Clear any stale sessions on login page mount to prevent cross-user contamination
  useEffect(() => {
    const supabase = getSupabaseClient();
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        // There's an active session but user is on login page — clear it
        supabase.auth.signOut({ scope: 'local' });
      }
    });
  }, []);

  // Set the flag for the next visit
  useEffect(() => {
    if (!localStorage.getItem('gc_guide_visited')) {
      localStorage.setItem('gc_guide_visited', 'true');
    }
  }, []);
  const inputClasses =
    'w-full border border-[#2C2731] rounded-xl px-4 py-3 text-sm bg-[#FFFCEF] placeholder:text-[#2C2731]/50 focus:outline-none focus:ring-2 focus:ring-[#2C2731] focus:border-[#2C2731] transition-colors';

  const handleSubmit = async (e: React.FormEvent) => {
    // console.log("handleSubmit triggered!", { email, password });

    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const supabase = getSupabaseClient();

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        // Check profile role - must be a guide
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role, terms_accepted')
          .eq('id', data.user?.id)
          .single();

        if (profileError) {
          console.error('Error checking profile:', profileError);
        }

        // Check if user is a guide
        if (profile && profile.role !== 'guide') {
          await supabase.auth.signOut();
          setError("Your account exists but you're not registered as a guide. Are you on the right page?");
          setLoading(false);
          return;
        }

        // If terms not accepted, redirect to terms page
        if (profile && !profile.terms_accepted) {
          navigate('/terms');
          setLoading(false);
          return;
        }

        // Check guide status to determine where to redirect
        const { data: guide, error: guideError } = await supabase
          .from('guides')
          .select('status')
          .eq('user_id', data.user?.id)
          .single();

        if (guideError) {
          console.error('Error checking guide status:', guideError);
          // If there's an error or no guide record, send to onboarding
          navigate('/onboarding');
        } else if (guide?.status === 'active') {
          // Only active guides go to dashboard
          navigate('/dashboard');
        } else {
          // All other statuses (pending, approved, etc.) go to onboarding
          navigate('/onboarding');
        }

        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFCEF] text-[#2C2731]">
      {/* Header */}
      <Header variant="login" />

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="max-w-md w-full text-center rounded-2xl px-8 py-10 shadow-sm">
          {/* Icon */}
          <div className="h-14 w-14 mx-auto flex items-center justify-center rounded-full bg-[#E8DFBD] text-[#2C2731] mb-8">
            <ArrowRightOnRectangleIcon className="h-6 w-6 text-[#2C2731]" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-medium text-[#2C2731] mb-2 font-recoleta">
            {isReturningUser ? 'Welcome Back' : 'Welcome'}
          </h1>
          <p className="text-[#2C2731]/80 mb-8">
            Sign in to your guide account to manage your seekers and community.
          </p>

          {/* Form */}
          <form className="text-left space-y-4" onSubmit={handleSubmit}>
            {error && (
              <div className="p-4 bg-[#2C2731] border border-[#FFFCEF] rounded-xl text-[#FFFCEF]">
                <div className="flex">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-[#FFFCEF]" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">
                      {error === 'Invalid login credentials' || error.includes('Invalid') || error.includes('credentials')
                        ? (
                          <>
                            Incorrect email or password. Please check your details and try again.{' '}
                            <a href="/auth/forgot-password" className="underline hover:text-[#E8DFBD] transition-colors">
                              Forgot password?
                            </a>
                          </>
                        )
                        : error}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[#2C2731] mb-1">Email Address</label>
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClasses}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#2C2731] mb-1">Password</label>
              <PasswordInput
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClasses}
              />
              <div className="text-right mt-2">
                <a
                  href="/auth/forgot-password"
                  className="text-sm text-[#2C2731]/70 hover:text-[#2C2731] transition-colors"
                >
                  Forgot password?
                </a>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#304031] text-sm font-semibold transition ${
                loading
                  ? 'bg-[#304031]/70 text-[#fef6d6] cursor-not-allowed'
                  : 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-[#FFFCEF] border-t-transparent rounded-full"></div>
                  Signing In...
                </>
              ) : (
                <>
                  <ArrowRightOnRectangleIcon className="h-5 w-5 text-[#fef6d6]" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Register button */}
          <div className="mt-4">
            <p className="text-sm text-center text-[#2C2731]/70 mb-3">
              Don't have an account yet?
            </p>
            <a
              href="/register"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#304031] bg-transparent text-[#304031] text-sm font-semibold transition hover:bg-[#304031]/5"
            >
              Create Account
            </a>
          </div>

          {/* Helper text */}
          <p className="text-xs text-[#2C2731]/60 mt-6">
            Secure login to access your dashboard, seeker management tools, and financial overview.
          </p>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
